import React, { useCallback, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { confirm, notify } from '../../services/dialog';
import PageHeader from '../../components/account/PageHeader';
import Icon from '../../components/ui/Icon';
import Text from '../../components/ui/Text';
import {
  addressName,
  formatAddress,
  isUnresolved,
  SavedAddress,
} from '../../data/places';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../../theme';

/**
 * The address book.
 *
 * ---------------------------------------------------------------------------
 * THE LIST IS REAL NOW
 * ---------------------------------------------------------------------------
 * It used to render the seed array straight from `data/places`, which is what
 * made saving an address look broken: the flow saved, the header changed, and
 * this page still showed the same two rows it shipped with. The book comes
 * from `App` and is written to disk on every save, so anything added in the
 * location flow is here - and still here after a relaunch.
 *
 * Deleting still does nothing, and is marked below. It needs the same wiring
 * this list just got, plus a confirmation, and it is not worth doing until an
 * account service owns the book.
 *
 * There is no delivery-instructions field here. One used to sit under the
 * list; it held what you typed and lost it on unmount, saving nothing and
 * reaching no booking. Per-address notes belong on the address itself - see
 * `SavedAddress.note`, which the location flow already writes.
 */

type Props = {
  onBack: () => void;
  /** Starts the location flow for an address not on file. */
  onAdd: () => void;
  /** Every address on file, newest first. */
  addresses: SavedAddress[];
  /** The one currently in use, so it can be marked. */
  current?: string;
  /** Re-pick an address on the map. */
  onEdit: (address: SavedAddress) => void;
  /**
   * Look the address up again from its stored pin and write back what comes
   * out. For rows saved as a bare "Pinned location" - see `isUnresolved`.
   *
   * Rejects on failure so the row can say so: a repair that silently does
   * nothing looks identical to one that was never tapped.
   */
  onRepair: (address: SavedAddress) => Promise<void>;
  /**
   * Remove one for good. Rejects on failure, and the screen reports it -
   * a delete that quietly leaves the row on screen is worse than an error.
   */
  onDelete: (address: SavedAddress) => Promise<void>;
  /**
   * Make this the default. Rejects on failure - the mark otherwise flips back
   * on its own the next time the list reloads, with nothing on screen to say
   * why the tap "didn't work".
   */
  onSetDefault: (address: SavedAddress) => Promise<void>;
};

function SavedAddressesScreen({
  onBack,
  onAdd,
  addresses,
  current,
  onEdit,
  onDelete,
  onRepair,
  onSetDefault,
}: Props) {
  const resp = useResponsive();
  /** Which row's overflow menu is open, or null. */
  const [menu, setMenu] = useState<string | null>(null);
  /** The id being deleted, so its row can say so and not be tapped twice. */
  const [busy, setBusy] = useState<string | null>(null);
  /** The id being marked default, so its radio can show it is working rather
      than looking like the tap did nothing for the round trip. */
  const [markingDefault, setMarkingDefault] = useState<string | null>(null);
  /** The id being looked up again, so the row can say so. */
  const [repairing, setRepairing] = useState<string | null>(null);

  const handleRepair = useCallback(
    (addr: SavedAddress) => {
      if (repairing) {
        return;
      }
      setRepairing(addr.place.id);
      onRepair(addr)
        .catch((failure: unknown) =>
          notify(
            'Could not find this address',
            failure instanceof Error
              ? failure.message
              : 'We could not read an address for that pin. Try Edit and move it slightly.',
          ),
        )
        .finally(() => setRepairing(null));
    },
    [onRepair, repairing],
  );

  const handleSetDefault = useCallback(
    (addr: SavedAddress) => {
      if (addr.isDefault || markingDefault) {
        return;
      }
      setMarkingDefault(addr.place.id);
      onSetDefault(addr)
        .catch((failure: unknown) =>
          notify(
            'Could not set default',
            failure instanceof Error
              ? failure.message
              : 'Try again in a moment.',
          ),
        )
        .finally(() => setMarkingDefault(null));
    },
    [markingDefault, onSetDefault],
  );

  /**
   * Deleting, with the one confirmation it deserves.
   *
   * An address is a few minutes of typing and a pin somebody placed; an
   * accidental tap on a menu item should not be able to spend that. The
   * failure is reported rather than swallowed - see `onDelete`.
   */
  const confirmDelete = useCallback(
    async (address: SavedAddress) => {
      const remove = await confirm({
        title: 'Delete this address?',
        message: formatAddress(address),
        confirmLabel: 'Delete',
        cancelLabel: 'Keep',
      });
      if (!remove) return;

      setBusy(address.place.id);
      onDelete(address)
        .catch((failure: unknown) =>
          notify(
            'Could not delete',
            failure instanceof Error
              ? failure.message
              : 'That address is still on file. Try again.',
          ),
        )
        .finally(() => setBusy(null));
    },
    [onDelete],
  );

  return (
    <View style={styles.root}>
      <PageHeader
        title="Saved Addresses"
        onBack={onBack}
        action="+ Add New"
        onAction={onAdd}
      />

      {/*
        `flex: 1` on the scroller itself, not only on its content.

        Inside a flex column a ScrollView with no flex of its own sizes to its
        content and overflows the parent, so the foot of a long list is simply
        cut off - worst on whichever filter shows the most. Flexed, it takes the
        space left under the header and scrolls inside it.
      */}
      <ScrollView
        style={styles.scroller}
        /* Zero on a phone; on a tablet it holds the page to a readable
           column and centres it. See `theme/responsive.ts`. */
        contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {addresses.length === 0 ? (
          <View style={styles.empty}>
            <Icon name="pin" size={32} color={colors.borderStrong} />
            <Text variant="subtitle" align="center">
              No addresses saved
            </Text>
            <Text variant="bodySm" align="center" color={colors.inkMuted}>
              Add one and it will be ready the next time you book.
            </Text>
          </View>
        ) : (
          <View style={styles.card}>
            {addresses.map((address, i) => {
              const name = addressName(address);
              const open = menu === address.place.id;
              /* The one the app is booking against right now, which is not
                 necessarily the one marked default - picking another in the
                 cart changes this and not that. */
              const inUse = formatAddress(address) === current;

              const settingThisDefault = markingDefault === address.place.id;

              return (
                <View
                  key={address.place.id}
                  style={i > 0 ? styles.rowDivided : null}
                >
                  <View style={styles.row}>
                    {/*
                    The radio IS the "make this default" control - tapping the
                    mark sets it, rather than the mark being decoration and a
                    separate badge further down saying which one already is.
                    Disabled once it is already the default: there is no
                    "unset" action, since the server always has exactly one.
                  */}
                    <Pressable
                      accessibilityRole="radio"
                      accessibilityLabel={
                        address.isDefault
                          ? `${name} is the default address`
                          : `Set ${name} as default`
                      }
                      accessibilityState={{ checked: address.isDefault }}
                      onPress={() => handleSetDefault(address)}
                      disabled={address.isDefault || settingThisDefault}
                      hitSlop={8}
                      style={({ pressed }) => [
                        styles.mark,
                        address.isDefault ? styles.markDefault : null,
                        pressed && !address.isDefault ? styles.pressed : null,
                      ]}
                    >
                      <Icon
                        name={address.isDefault ? 'checkCircle' : 'home'}
                        size={17}
                        color={
                          address.isDefault ? colors.onPrimary : colors.primary
                        }
                      />
                    </Pressable>

                    <View style={styles.body}>
                      <View style={styles.nameRow}>
                        <Text variant="subtitle">{name}</Text>
                        {inUse ? (
                          <View style={[styles.badge, styles.badgeInUse]}>
                            <Text variant="caption" color={colors.onPrimary}>
                              In use
                            </Text>
                          </View>
                        ) : null}
                        {address.isDefault ? (
                          <View style={styles.badge}>
                            <Text variant="caption" color={colors.primary}>
                              Default
                            </Text>
                          </View>
                        ) : null}
                      </View>

                      <Text variant="caption" color={colors.inkMuted}>
                        {formatAddress(address)}
                      </Text>

                      {/*
                      A row that is only a pin, and the one tap that fixes it.

                      These were saved before the location flow refused to
                      write an unresolved pin - the geocode failed, the failure
                      was swallowed, and the coordinates went in as though they
                      were a street. The pin itself is good, so looking it up
                      again is usually all it takes; Edit is the fallback for
                      when the pin really is somewhere with no address.
                    */}
                      {isUnresolved(address) ? (
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={`Find the address for this pin`}
                          onPress={() => handleRepair(address)}
                          disabled={repairing === address.place.id}
                          hitSlop={8}
                          style={styles.repair}
                        >
                          <Icon name="pin" size={13} color={colors.primary} />
                          <Text variant="caption" color={colors.primary}>
                            {repairing === address.place.id
                              ? 'Finding address…'
                              : 'Find address'}
                          </Text>
                        </Pressable>
                      ) : null}
                      {address.note ? (
                        <Text variant="caption" color={colors.inkMuted}>
                          {address.note}
                        </Text>
                      ) : null}
                    </View>

                    <View style={styles.tools}>
                      {/* One control, not two. A gear beside an overflow offered
                        the same thing twice - the gear opened the location
                        flow, which is exactly what "Edit" in the menu does. */}
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={`More options for ${name}`}
                        accessibilityState={{ expanded: open }}
                        onPress={() => setMenu(open ? null : address.place.id)}
                        hitSlop={8}
                        style={({ pressed }) =>
                          pressed ? styles.pressed : null
                        }
                      >
                        <Icon name="more" size={17} color={colors.inkMuted} />
                      </Pressable>
                    </View>
                  </View>

                  {/*
                    The overflow, in the flow under its row rather than floating
                    over it.

                    Absolutely positioned it sat on top of the next address -
                    the card does not grow for something taken out of the flow,
                    so the note and the entry below it occupied the same pixels.
                    In the flow the card opens to make room.

                    There is no anchored-menu primitive in the project, and a
                    modal for three options would cover the list you are
                    choosing within.
                  */}
                  {open ? (
                    <View style={styles.menu}>
                      {/*
                        Hidden on the address that already IS the default.
                        There is no "unset" - the server keeps exactly one - so
                        the row would be a no-op that looks tappable. The radio
                        to the left is disabled for the same reason.
                      */}
                      {address.isDefault ? null : (
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={`Set ${name} as default`}
                          onPress={() => {
                            setMenu(null);
                            handleSetDefault(address);
                          }}
                          disabled={settingThisDefault}
                          style={({ pressed }) => [
                            styles.menuItem,
                            pressed ? styles.pressed : null,
                          ]}
                        >
                          <Icon
                            name="checkCircle"
                            size={16}
                            color={colors.primary}
                          />
                          <Text variant="bodySm" color={colors.primary}>
                            {settingThisDefault ? 'Setting…' : 'Use as default'}
                          </Text>
                        </Pressable>
                      )}

                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={`Edit ${name}`}
                        onPress={() => {
                          setMenu(null);
                          onEdit(address);
                        }}
                        style={({ pressed }) => [
                          styles.menuItem,
                          pressed ? styles.pressed : null,
                        ]}
                      >
                        <Icon name="pin" size={16} color={colors.ink} />
                        <Text variant="bodySm">Edit</Text>
                      </Pressable>

                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={`Delete ${name}`}
                        onPress={() => {
                          setMenu(null);
                          confirmDelete(address);
                        }}
                        disabled={busy === address.place.id}
                        style={({ pressed }) => [
                          styles.menuItem,
                          pressed ? styles.pressed : null,
                        ]}
                      >
                        <Icon name="trash" size={16} color={colors.danger} />
                        <Text variant="bodySm" color={colors.danger}>
                          {busy === address.place.id ? 'Deleting…' : 'Delete'}
                        </Text>
                      </Pressable>
                    </View>
                  ) : null}
                </View>
              );
            })}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroller: {
    flex: 1,
  },
  page: {
    /* Vertical only: the horizontal gutter comes from `resp.page` on the
       contentContainer, which also carries the tablet inset. `padding`
       here was silently overridden by it. */
    paddingVertical: screenPadding,
    gap: spacing.lg,
    // Clears the floating tab bar, which overlays the bottom of the screen.
    paddingBottom: 120,
  },
  card: {
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    padding: spacing.md,
  },
  /* On the row rather than the card, so the first entry has no rule above it. */
  rowDivided: {
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  mark: {
    width: 34,
    height: 34,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  /* Filled rather than just swapping the glyph - a lone icon change on a
     34x34 square is too small a difference to read as "this one is on". */
  markDefault: {
    backgroundColor: colors.primary,
  },
  body: {
    flex: 1,
    gap: 3,
  },
  /* Inline under the pin text it repairs. */
  repair: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  badgeInUse: {
    backgroundColor: colors.primary,
  },
  badge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 1,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceAccent,
  },
  tools: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingTop: 2,
  },
  /* Indented to the row's text, so it reads as belonging to that entry rather
     than to the card. */
  menu: {
    paddingLeft: spacing.md + 34 + spacing.md,
    paddingRight: spacing.md,
    paddingBottom: spacing.md,
  },

  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.sm,
  },
  empty: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xxxl,
    paddingHorizontal: spacing.xl,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default SavedAddressesScreen;
